// Vollea — Share Card Modal. Wraps a <WinnerCard /> at full 1080×1920 size,
// scales the preview down to fit the viewport via CSS transform, and exposes
// Download PNG + Native Share buttons. PNG export uses html-to-image, which
// rasterises the DOM node into a high-resolution data URL on the client.
import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { IoClose, IoDownloadOutline, IoShareSocialOutline } from "react-icons/io5";
import WinnerCard, { CARD_DIMENSIONS } from "./WinnerCard";
import { C, SP, R } from "../lib/theme";

const canNativeShare = () =>
  typeof navigator !== "undefined" &&
  typeof navigator.share === "function" &&
  typeof navigator.canShare === "function";

export default function ShareCardModal({ open, onClose, fileBaseName = "vollea-card", ...cardProps }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0.35);
  const [msg, setMsg] = useState("");

  // Scale the 1080×1920 card to fit the viewport while leaving room for the
  // button row + safe-area padding. Recomputes on resize.
  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const padX = 32;
      const reservedY = 120; // top close-bar + bottom action row
      const maxW = (window.innerWidth - padX * 2) / CARD_DIMENSIONS.width;
      const maxH = (window.innerHeight - reservedY) / CARD_DIMENSIONS.height;
      setScale(Math.min(maxW, maxH, 0.55));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open]);

  const renderBlob = async () => {
    const node = cardRef.current;
    if (!node) throw new Error("Card not ready");
    // Pixel ratio 1 because the source is already 1080×1920 (effectively 2x).
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: 1,
      backgroundColor: C.bg,
    });
    const res = await fetch(dataUrl);
    return res.blob();
  };

  const handleDownload = async () => {
    setBusy(true); setMsg("");
    try {
      const blob = await renderBlob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${fileBaseName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      setMsg("Downloaded");
    } catch (e) {
      setMsg(e?.message || "Could not generate image");
    } finally {
      setBusy(false);
    }
  };

  const handleNativeShare = async () => {
    setBusy(true); setMsg("");
    try {
      const blob = await renderBlob();
      const file = new File([blob], `${fileBaseName}.png`, { type: "image/png" });
      if (canNativeShare() && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Vollea", text: "Check this out on Vollea" });
      } else {
        // Fallback: copy a data URL to clipboard isn't broadly supported for
        // PNGs, so we just download in that case.
        await handleDownload();
        return;
      }
    } catch (e) {
      // User aborting native share throws — treat as silent dismiss.
      if (e && e.name !== "AbortError") setMsg(e.message || "Share failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const showShare = canNativeShare();

  return (
    <div
      data-testid="share-card-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.85)",
        display: "flex", flexDirection: "column",
      }}
      onClick={onClose}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: SP.lg, color: C.text }}>
        <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.5 }}>Share card</div>
        <button
          type="button"
          data-testid="share-card-close"
          onClick={onClose}
          style={{ width: 40, height: 40, borderRadius: 20, background: "transparent", border: `1px solid ${C.border}`, color: C.text, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          <IoClose size={22} />
        </button>
      </div>

      <div
        style={{
          flex: 1, display: "flex", justifyContent: "center", alignItems: "center",
          overflow: "hidden", padding: `0 ${SP.lg}px`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: CARD_DIMENSIONS.width * scale,
            height: CARD_DIMENSIONS.height * scale,
            position: "relative",
            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            borderRadius: R.lg,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute", top: 0, left: 0,
              transformOrigin: "top left",
              transform: `scale(${scale})`,
            }}
          >
            <WinnerCard ref={cardRef} {...cardProps} />
          </div>
        </div>
      </div>

      <div
        style={{ padding: SP.lg, display: "flex", flexDirection: "column", gap: SP.sm }}
        onClick={(e) => e.stopPropagation()}
      >
        {msg ? (
          <div data-testid="share-card-msg" style={{ color: msg === "Downloaded" ? C.lime : C.error, fontSize: 13, textAlign: "center" }}>{msg}</div>
        ) : null}
        <div style={{ display: "flex", gap: SP.sm }}>
          {showShare && (
            <button
              type="button"
              onClick={handleNativeShare}
              disabled={busy}
              data-testid="share-card-native-share"
              style={{ flex: 1, height: 52, borderRadius: R.md, background: C.lime, color: C.onLime, fontWeight: 800, fontSize: 13, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", cursor: "pointer" }}
            >
              <IoShareSocialOutline size={18} /> Share
            </button>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            data-testid="share-card-download"
            style={{ flex: 1, height: 52, borderRadius: R.md, background: showShare ? C.surface2 : C.lime, color: showShare ? C.text : C.onLime, border: showShare ? `1px solid ${C.borderStrong}` : "none", fontWeight: 800, fontSize: 13, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" }}
          >
            <IoDownloadOutline size={18} /> Download PNG
          </button>
        </div>
      </div>
    </div>
  );
}
